import React, { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import "./App.css";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import TermsAndPrivacy from "./pages/terms-and-privacy";
import { Toaster } from "./components/ui/toaster";
import Dashboard from "./pages/Dashboard";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { SupabaseAuthProvider } from "./context/SupabaseAuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import SplashScreen from "./components/SplashScreen";
import MyReports from "./pages/MyReports";
import CookieConsent from "./components/CookieConsent";
import SubscriptionPlans from "./pages/SubscriptionPlans";
import MyRegions from "./pages/MyRegions";
import Profile from "./pages/Profile";
import KnowledgeHub from "./pages/KnowledgeHub";
import Projects from "./pages/Projects";
import Pricing from "./pages/Pricing";
import { ProfessionalPlanPage } from "./pages/ProfessionalPlan";
import { PurchaseCreditsPage } from "./pages/PurchaseCredits";
import HelpPage from "./pages/Help";
import UsageDetails from "./pages/UsageDetails";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import AirQualityTester from "./components/test/AirQualityTester";
import AcceptInvitation from "./pages/AcceptInvitation";
import Messages from "./pages/Messages";
import { AuthModalProvider, useAuthModal } from "./context/SupabaseAuthContext";
import AuthModal from "./components/auth/AuthModal";
import OnboardingModal from "./components/onboarding/OnboardingModal";
import { supabase } from "./integrations/supabase/client";
import MobileWarning from "./components/MobileWarning";

// Create a client
const queryClient = new QueryClient();

export default function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  // Check if this is the first time the app has loaded in this session
  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem("hasLoadedBefore");

    if (hasLoadedBefore) {
      setShowSplash(false);
    } else {
      // Set flag in session storage
      sessionStorage.setItem("hasLoadedBefore", "true");
    }
  }, []);

  // Allow opening the onboarding via URL query param: ?showOnboarding=1
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("showOnboarding") === "1") {
        setShowOnboarding(true);
      }
    } catch (e) {
      // ignore (server rendering or non-browser environments)
    }
  }, []);

  // Check for user session and onboarding status
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Check for Google OAuth onboarding trigger
      const shouldTriggerOnboarding =
        sessionStorage.getItem("triggerOnboarding");
      const onboardingEmail = sessionStorage.getItem("onboardingEmail");

      if (shouldTriggerOnboarding === "true" && onboardingEmail) {
        sessionStorage.removeItem("triggerOnboarding");
        sessionStorage.removeItem("onboardingEmail");
        setUserEmail(onboardingEmail);
        setShowOnboarding(true);
        return;
      }

      // If user is logged in but hasn't completed onboarding
      if (session?.user && !session.user.user_metadata?.onboarding_completed) {
        setUserEmail(session.user.email || "");
        setShowOnboarding(true);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Check onboarding status on auth state change
      if (session?.user && !session.user.user_metadata?.onboarding_completed) {
        setUserEmail(session.user.email || "");
        setShowOnboarding(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSplashFinished = () => {
    setShowSplash(false);
  };

  return (
    <ThemeProvider>
      <CurrencyProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <SupabaseAuthProvider>
              <AuthModalProvider>
                {showSplash ? (
                  <SplashScreen onFinished={handleSplashFinished} />
                ) : (
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route
                      path="/accept-invitation"
                      element={<AcceptInvitation />}
                    />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/my-reports" element={<MyReports />} />
                    <Route path="/my-regions" element={<MyRegions />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/messages" element={<Messages />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route
                      path="/subscription-plans"
                      element={<SubscriptionPlans />}
                    />
                    <Route
                      path="/professional"
                      element={<ProfessionalPlanPage />}
                    />
                    <Route
                      path="/purchase-credits"
                      element={<PurchaseCreditsPage />}
                    />
                    <Route path="/dashboard/usage" element={<UsageDetails />} />
                    <Route
                      path="/payment/success"
                      element={<PaymentSuccess />}
                    />
                    <Route
                      path="/payment/cancelled"
                      element={<PaymentCancelled />}
                    />
                    <Route path="/profile" element={<Profile />} />
                    <Route
                      path="/terms-and-privacy"
                      element={<TermsAndPrivacy />}
                    />
                    <Route path="/knowledge-hub" element={<KnowledgeHub />} />
                    <Route path="/help" element={<HelpPage />} />
                    <Route
                      path="/test-air-quality"
                      element={<AirQualityTester />}
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                )}
                <AuthModalWrapper
                  setShowOnboarding={setShowOnboarding}
                  setUserEmail={setUserEmail}
                />
                {/* Dev-only trigger: visible only in dev builds */}
                {import.meta?.env?.DEV && (
                  <div
                    style={{
                      position: "fixed",
                      bottom: 16,
                      left: 16,
                      zIndex: 9999,
                    }}
                  >
                    <button
                      onClick={() => {
                        setUserEmail("dev@local");
                        setShowOnboarding(true);
                      }}
                      className="rounded bg-blue-600 text-white px-3 py-1 text-sm"
                    >
                      Show Onboarding (dev)
                    </button>
                  </div>
                )}
                <OnboardingModal
                  showOnboarding={showOnboarding}
                  setShowOnboarding={setShowOnboarding}
                  userEmail={userEmail}
                />
                <Toaster />
                <CookieConsent />
                <MobileWarning />
              </AuthModalProvider>
            </SupabaseAuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </CurrencyProvider>
    </ThemeProvider>
  );
}

// Helper to use context inside App
function AuthModalWrapper({
  setShowOnboarding,
  setUserEmail,
}: {
  setShowOnboarding: (show: boolean) => void;
  setUserEmail: (email: string) => void;
}) {
  const { showLogin, setShowLogin } = useAuthModal();
  return (
    <AuthModal
      showLogin={showLogin}
      setShowLogin={setShowLogin}
      setShowOnboarding={setShowOnboarding}
      setUserEmail={setUserEmail}
    />
  );
}
